import {Paper, Typography, useTheme} from "@mui/material";
import {ManagerStats} from "arteco-api-client-ts";
import {Box} from "@mui/system";
import React from "react";


export interface EfficiencyIndicatorsProps {
	stats: ManagerStats;
}

export const EfficiencyIndicators = ({stats}: EfficiencyIndicatorsProps) => {
	interface Item {
		title: string;
		ratio: number;
		hours: number;
		diff?: number;
		description: string;
	}

	const calcBudgetRatio = (stats: ManagerStats, ratio: boolean, prev: boolean = false): number => {
		const budgetNoTm = stats.budgets?.filter(b => (b.totalBudgetHours || 0) > 0) || [];
		const budget = budgetNoTm.reduce((acc, act) => acc + (act.totalBudgetHours || 0), 0);
		const dedicated = budgetNoTm.reduce((acc, act) => acc + (act.dedicatedHours || 0), 0);

		if (ratio) {
			return parseFloat(((dedicated - budget) / (budget + 1)).toFixed(2)) * 100;
		} else {
			return budget - dedicated;
		}

	}

	const calcOccupancyRatio = (stats: ManagerStats, ratio: boolean, prev: boolean = false): number => {

		const actualMonth = new Date().getMonth() + 1;
		const previousMonths = stats.months?.filter(m => (m.month || 0) < actualMonth) || [];
		const selectedMonths = (prev
			? previousMonths
			: stats.months) || [];

		const totalWorked = selectedMonths.reduce((acc, act) => acc + (act.dedication?.workedHours || 0), 0) || 0;
		const totalUnassigned = selectedMonths.reduce((acc, act) => acc + (act.dedication?.unassignedHours || 0), 0) || 0;

		if (ratio) {
			return parseFloat((totalUnassigned / (totalWorked + 1)).toFixed(2)) * 100;
		} else {
			return -totalUnassigned;
		}
	}

	const diff = (a: number, b: number): number | undefined => {
		if (b == 0) {
			return undefined;
		}
		return (a - b) / b;
	};

	const efficiencies: Item[] = [
		{
			title: 'Desvío de Ocupación',
			ratio: calcOccupancyRatio(stats, true),
			hours: calcOccupancyRatio(stats, false),
			diff: diff(calcOccupancyRatio(stats, true, true), calcOccupancyRatio(stats, true)),
			description: "Horas sin asignar (sin presupuesto, ni T&M) vs al total de horas del equipo."
		},
		{
			title: 'Desvío de Presupuestación',
			ratio: calcBudgetRatio(stats, true),
			hours: calcBudgetRatio(stats, false),
			diff: undefined,
			description: "Horas planificadas de los presupuestos vs la dedicación importada de redmine."
		}
	];

	return (
		<Box display="flex" gap={2} flexDirection={{xs: 'column', md: 'row'}}>
			{efficiencies.map((item, idx) => (
				<EfficiencyCard key={idx} title={item.title}
								ratio={item.ratio}
								hours={item.hours}
								diff={item.diff}
								description={item.description}/>
			))}
		</Box>
	);
};

export interface EfficiencyCardProps {
	title: string;
	description: string;
	ratio: number;
	hours: number;
	diff?: number;
}

export const EfficiencyCard = ({title, description, ratio, hours, diff}: EfficiencyCardProps) => {
	const theme = useTheme();
	return (
		<Paper elevation={3} sx={{p: 2, width: '100%', borderRadius: 2}}>
			<Typography variant="h2" sx={{
				fontWeight: "bolder",
				color: ratio <= 0
					? theme.palette.success.main
					: ratio >= 20
						? theme.palette.error.main
						: theme.palette.warning.main
			}}>
				{ratio.toFixed(0)}%
				{diff != undefined && Math.abs(diff) > 1 && (<sup style={{fontSize: "0.5em"}}>&nbsp;{diff?.toFixed(0)}%</sup>)}
			</Typography>
			<Typography variant="h6" gutterBottom>
				{title}
			</Typography>
			<Typography variant="body2" gutterBottom>
				{description}
			</Typography>
			<Typography mt={1} variant="body2" gutterBottom>
				{hours.toFixed(1)} horas
				{diff != undefined && Math.abs(diff) > 1 && (
					<>, con una diferencia de {diff.toFixed(1)}% respecto al mes anterior
					</>
				)}
			</Typography>
		</Paper>
	);
}
